/**
 * The use-case description (ADR-0026 amendment 2026-10-17, §AU;
 * docs/architecture/use-case-description.md).
 *
 * One entry per use case. Every decision in code asks a PROPERTY of an entry
 * ("is this booked by the hour?", "is this a room?"), never the use case's
 * name — so a new use case such as `hall` is one entry here, not a new branch
 * in every place that once compared against "venue". `scripts/use-case-guard`
 * fails on a new name comparison outside these files.
 *
 * The property meanings are written once, in
 * `shared/use-cases/use-case-description.json`. This file is a typed COPY of
 * it, as is `frontend/admin-ui/src/utils/useCases.ts`. The three backends hold
 * no copy: they build from `shared/src/useCases.ts` (`@kolula/shared`,
 * docs/plans/shared-rules-package.md). Each copy's test, and each backend's,
 * compares it to the JSON, so they cannot drift; a change is made in the JSON,
 * the package and both frontend copies in one PR.
 *
 * The types have no defaults: a new property needs a value for every use case,
 * and the compiler asks for each one.
 *
 * `live` = the database knows the value. Everything here except
 * `describeAnyUseCase` answers for LIVE use cases only and treats the rest as
 * unknown, because Prisma rejects an enum value the database does not have:
 * `hall` went live with its enum migration (completion plan W1-1, PR #3397);
 * a use case added later starts with live = false and is invisible to every
 * reader until its migration is deployed.
 */

export type UseCase = "venue" | "apartment" | "hall" | "rental" | "storage" | "sup" | "keys" | "p2p" | "parcel";

export type Template = "space" | "stationUnit" | "handover";

/**
 * Which per-category settings the use case's booking screens honour
 * (#3429 step 2). Carried by the space AND the station-unit block, so a gate
 * asks the property, never the template. On a station
 * unit a `false` means "no screen of this use case reads it yet", not "does
 * not apply" (PO, 2026-10-30): it flips in the step that builds the reader.
 */
export interface CategorySettingProperties {
  /** The booking asks for the event (a list, or free text). */
  eventTypes: boolean;
  /** The category's note is shown on the booking step. */
  categoryNote: boolean;
  /** The venue rate knobs (weekend mode, half day) apply. */
  rateUnits: boolean;
  /** The usage-pattern choices are offered: entry sequence, head-count word, sign-in first (#3035, #3069). */
  usagePatterns: boolean;
  /** A further category may carry its own cancellation rule (PO decision 5, 2026-10-19). */
  categoryCancellation: boolean;
}

/** What a booking of the SPACE template is like. */
export interface SpaceProperties extends CategorySettingProperties {
  /** One booking unit: a time window on a day, or a stay of nights. */
  timeUnit: "hour" | "night";
  /** The room-family booking screens, mails and pricing rule it uses. */
  flow: "event" | "stay";
  /** A whole-day booking window: lead time and buffer in days, day-granular, full-day defaults (was FULL_DAY_USE_CASES). */
  windowInDays: boolean;
  /** The default follow-up buffer after a booking, in days, when none is configured. */
  followUpDefaultDays: number;
  /** A minimum number of nights applies. */
  minStay: boolean;
  /** Without a configured value, a booking is direct rather than a reservation request. */
  directBookingDefault: boolean;
  /** The room may be divided into segments. */
  divisible: boolean;
  /** The allocations tab is offered before the first window, once the series season is set (W2-3, PO 2026-10-25). */
  allocationWindows: boolean;
}

/** What a booking of the STATION-UNIT template is like (#3429 step 1, PO 2026-10-30). */
export interface StationUnitProperties extends CategorySettingProperties {
  /** One booking is start plus N minutes; hours or days is the price category's price-scale rows, not the use case. */
  timeUnit: "duration";
  /** The booking ends with a return the customer drives (finish rental). */
  returnStep: boolean;
  /** The return asks for a photo when the tenant configured nothing (the photo itself is a tenant / category setting). */
  returnPhotoDefault: boolean;
  /** The hand-back grace period and the overtime allowance apply (was OVERTIME_USE_CASES). */
  gracePeriod: boolean;
  /** The starting value of a product category's grace period, in minutes (per tenant and category: #1641). */
  gracePeriodDefaultMinutes: number;
  /** The first slot offered is the quarter-hour already started: customers book standing at the station (walk-up; was STARTED_QUARTER_TENANTS). */
  startedQuarter: boolean;
}

export interface UseCaseDescription {
  live: boolean;
  /** A tenant may switch it on under Use cases. */
  enableable: boolean;
  template: Template;
  /** The selection mode a new product starts with (D11); the tenant sets it per product (ADR-0026 §AP). */
  selectionModeDefault: "individual" | "pooled";
  /** Its products can be filed under product categories, and its home tile splits per category (#3429 step 3). */
  productCategories: boolean;
  /** Set for the space template, `null` for every other. */
  space: Readonly<SpaceProperties> | null;
  /** Set for the station-unit template, `null` for every other. */
  stationUnit: Readonly<StationUnitProperties> | null;
}

export const USE_CASE_DESCRIPTIONS: Readonly<Record<UseCase, Readonly<UseCaseDescription>>> = Object.freeze({
  venue: Object.freeze({
    live: true,
    enableable: true,
    template: "space",
    selectionModeDefault: "individual",
    productCategories: true,
    space: Object.freeze({
      timeUnit: "hour",
      flow: "event",
      windowInDays: true,
      followUpDefaultDays: 0,
      minStay: false,
      directBookingDefault: false,
      eventTypes: true,
      categoryNote: true,
      rateUnits: true,
      usagePatterns: true,
      categoryCancellation: true,
      divisible: true,
      allocationWindows: false,
    }),
    stationUnit: null,
  }),
  apartment: Object.freeze({
    live: true,
    enableable: true,
    template: "space",
    selectionModeDefault: "individual",
    productCategories: true,
    space: Object.freeze({
      timeUnit: "night",
      flow: "stay",
      windowInDays: true,
      followUpDefaultDays: 1,
      minStay: true,
      directBookingDefault: false,
      eventTypes: false,
      categoryNote: false,
      rateUnits: false,
      usagePatterns: true,
      categoryCancellation: true,
      divisible: false,
      allocationWindows: false,
    }),
    stationUnit: null,
  }),
  hall: Object.freeze({
    live: true,
    enableable: true,
    template: "space",
    selectionModeDefault: "individual",
    productCategories: true,
    space: Object.freeze({
      timeUnit: "hour",
      flow: "event",
      windowInDays: true,
      followUpDefaultDays: 0,
      minStay: false,
      directBookingDefault: false,
      eventTypes: true,
      categoryNote: true,
      rateUnits: true,
      usagePatterns: true,
      categoryCancellation: true,
      divisible: true,
      allocationWindows: true,
    }),
    stationUnit: null,
  }),
  rental: Object.freeze({
    live: true,
    enableable: true,
    template: "stationUnit",
    selectionModeDefault: "pooled",
    productCategories: true,
    space: null,
    stationUnit: Object.freeze({
      timeUnit: "duration",
      returnStep: true,
      returnPhotoDefault: true,
      gracePeriod: true,
      gracePeriodDefaultMinutes: 15,
      startedQuarter: false,
      eventTypes: false,
      categoryNote: false,
      rateUnits: false,
      usagePatterns: false,
      categoryCancellation: false,
    }),
  }),
  storage: Object.freeze({
    live: true,
    enableable: true,
    template: "stationUnit",
    selectionModeDefault: "pooled",
    productCategories: true,
    space: null,
    stationUnit: Object.freeze({
      timeUnit: "duration",
      returnStep: true,
      returnPhotoDefault: false,
      gracePeriod: true,
      gracePeriodDefaultMinutes: 15,
      startedQuarter: true,
      eventTypes: false,
      categoryNote: false,
      rateUnits: false,
      usagePatterns: false,
      categoryCancellation: false,
    }),
  }),
  sup: Object.freeze({
    live: true,
    enableable: true,
    template: "stationUnit",
    selectionModeDefault: "pooled",
    productCategories: true,
    space: null,
    stationUnit: Object.freeze({
      timeUnit: "duration",
      returnStep: true,
      returnPhotoDefault: true,
      gracePeriod: true,
      gracePeriodDefaultMinutes: 15,
      startedQuarter: false,
      eventTypes: false,
      categoryNote: false,
      rateUnits: false,
      usagePatterns: false,
      categoryCancellation: false,
    }),
  }),
  keys: Object.freeze({
    live: true,
    enableable: false,
    template: "handover",
    selectionModeDefault: "pooled",
    productCategories: false,
    space: null,
    stationUnit: null,
  }),
  p2p: Object.freeze({
    live: true,
    enableable: false,
    template: "handover",
    selectionModeDefault: "pooled",
    productCategories: false,
    space: null,
    stationUnit: null,
  }),
  parcel: Object.freeze({
    live: true,
    enableable: false,
    template: "handover",
    selectionModeDefault: "pooled",
    productCategories: false,
    space: null,
    stationUnit: null,
  }),
});

/**
 * The entry for a stored or sent use case, case-insensitively, whether live
 * or not; `null` for anything unknown. Only for code that must know a
 * use case the database does not have yet (tests, the switch-on itself).
 */
export const describeAnyUseCase = (useCase: string | null | undefined): Readonly<UseCaseDescription> | null => {
  const key = String(useCase ?? "").trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(USE_CASE_DESCRIPTIONS, key)
    ? USE_CASE_DESCRIPTIONS[key as UseCase]
    : null;
};

/** The entry for a stored or sent LIVE use case, case-insensitively; `null` for anything else. */
export const describeUseCase = (useCase: string | null | undefined): Readonly<UseCaseDescription> | null => {
  const description = describeAnyUseCase(useCase);
  return description?.live ? description : null;
};

/** The space properties of a live use case, or `null` when it is not a space (or unknown). */
export const spaceOf = (useCase: string | null | undefined): Readonly<SpaceProperties> | null =>
  describeUseCase(useCase)?.space ?? null;

/** The station-unit properties of a live use case, or `null` when it is not a station unit (or unknown). */
export const stationUnitOf = (useCase: string | null | undefined): Readonly<StationUnitProperties> | null =>
  describeUseCase(useCase)?.stationUnit ?? null;

/** The room family: every live use case of the space template. */
export const isRoomFamily = (useCase: string | null | undefined): boolean =>
  describeUseCase(useCase)?.template === "space";

/** The LIVE use cases an entry matches, in description order — for lists and query filters. */
export const liveUseCasesWhere = (
  matches: (description: Readonly<UseCaseDescription>) => boolean,
): UseCase[] =>
  (Object.keys(USE_CASE_DESCRIPTIONS) as UseCase[]).filter(
    (useCase) => USE_CASE_DESCRIPTIONS[useCase].live && matches(USE_CASE_DESCRIPTIONS[useCase]),
  );

